import { computed, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '@/environments/environment';
import { CookieService } from '@/services/cookie/cookie.service';
import { isMetrikaHostAllowed } from '@/services/metrika/metrika.utils';
import { setCookie } from '@/utils/utils';

export const ANALYTICS_CONSENT_COOKIE = 'qspa_analytics_consent';

export type AnalyticsConsentStatus = 'unknown' | 'granted' | 'denied';

type AnalyticsConsentValue = 'granted' | 'denied';

const CONSENT_COOKIE_DAYS = 365;

@Injectable({ providedIn: 'root' })
export class AnalyticsConsentService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly cookieService = inject(CookieService);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private readonly consentStatusSignal = signal<AnalyticsConsentStatus>(
    this.readConsentFromCookie(),
  );

  readonly consentStatus = this.consentStatusSignal.asReadonly();

  readonly bannerVisible = computed(
    () => this.canMetrikaRunOnHost() && this.consentStatusSignal() === 'unknown',
  );

  readonly hasDecision = computed(() => this.consentStatusSignal() !== 'unknown');

  grant(): void {
    this.persistConsent('granted');
  }

  deny(): void {
    this.persistConsent('denied');
  }

  private persistConsent(value: AnalyticsConsentValue): void {
    this.consentStatusSignal.set(value);
    if (this.isBrowser) {
      setCookie(ANALYTICS_CONSENT_COOKIE, value, CONSENT_COOKIE_DAYS);
    }
  }

  private readConsentFromCookie(): AnalyticsConsentStatus {
    const raw = this.cookieService.get(ANALYTICS_CONSENT_COOKIE);
    if (raw === 'granted' || raw === 'denied') {
      return raw;
    }
    return 'unknown';
  }

  private canMetrikaRunOnHost(): boolean {
    if (!environment.metrika.enabled) {
      return false;
    }
    if (!this.isBrowser || typeof window === 'undefined') {
      return false;
    }
    return isMetrikaHostAllowed(window.location.hostname, environment.metrika.allowedHosts);
  }
}
